import { createElement, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ReflexMark } from '@reflex/ui/components/brand/ReflexMark';
import { useOrgKey, useActiveOrgSlug, usePluginSocket } from '@reflex/ui/client/context';
import { withOrgSlug } from '@reflex/shared/routes';
import { notificationsApi } from './api.js';
import type { ListNotificationsResponse, Notification } from '../shared/types.js';

function isExpired(n: Notification): boolean {
  return n.expiresAt !== null && n.expiresAt <= Date.now();
}

type OrgKey = ReturnType<typeof useOrgKey>;

export const notificationsKey = (org: OrgKey) => ['notifications', org] as const;
export const notificationKey = (org: OrgKey, id: string) => ['notifications', org, id] as const;

/**
 * Pure read of the user's notification feed. Returns only non-dismissed,
 * non-expired entries so the caller can render an inbox without re-filtering.
 * Schedules a timer to re-evaluate when the earliest future `expiresAt`
 * passes, so open idle tabs auto-hide expired notices without a manual
 * refresh. No side effects beyond the timer: the WS subscription and
 * "new notification" toast live in {@link useNotificationsLiveEffects},
 * mounted exactly once globally via the plugin's `globalUiElements` slot —
 * having it on every consumer would multiply toasts when the same
 * notification arrived.
 */
export function useNotifications() {
  const orgKey = useOrgKey();
  const [tick, setTick] = useState(0);

  const query = useQuery({
    queryKey: notificationsKey(orgKey),
    queryFn: () => notificationsApi.list(),
  });

  const visible = useMemo<Notification[]>(
    () => (query.data?.notifications ?? []).filter((n) => !n.dismissed && !isExpired(n)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query.data, tick],
  );

  // Schedule a re-render at the earliest future expiresAt so the notification
  // vanishes from the list without the user refreshing.
  useEffect(() => {
    const now = Date.now();
    const allNotifications = query.data?.notifications ?? [];
    const futureExpiries = allNotifications
      .filter((n) => !n.dismissed && n.expiresAt !== null && n.expiresAt > now)
      .map((n) => n.expiresAt as number);
    if (futureExpiries.length === 0) return;

    const earliest = Math.min(...futureExpiries);
    const delay = earliest - now;
    const timer = setTimeout(() => setTick((t) => t + 1), delay);
    return () => clearTimeout(timer);
  }, [query.data]);

  return { ...query, notifications: visible };
}

/**
 * Side-effect-only hook: subscribes to `notifications:created` WS pushes,
 * refetches the inbox query, and fires a sonner toast (with a "View" link
 * to the detail page) for every genuinely new non-dismissed notification.
 *
 * Initial fetches are silent — a freshly-loaded inbox shouldn't toast for
 * every backlog entry the user hasn't seen yet.
 *
 * **Mount exactly once.** Multiple mounts would duplicate every toast.
 */
export function useNotificationsLiveEffects() {
  const orgKey = useOrgKey();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const slug = useActiveOrgSlug();
  const socket = usePluginSocket();

  const query = useQuery({
    queryKey: notificationsKey(orgKey),
    queryFn: () => notificationsApi.list(),
  });

  // Track every id we've already shown to the user across mounts of this
  // hook. `null` means "we have not received an initial response yet";
  // first response seeds the set silently so we don't toast for backlog.
  const seenIdsRef = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!query.data) return;
    const ids = query.data.notifications
      .filter((n) => !n.dismissed && !isExpired(n))
      .map((n) => n.id);
    if (seenIdsRef.current === null) {
      seenIdsRef.current = new Set(ids);
      return;
    }
    for (const n of query.data.notifications) {
      if (n.dismissed || isExpired(n)) continue;
      if (seenIdsRef.current.has(n.id)) continue;
      seenIdsRef.current.add(n.id);
      toast('New notification', {
        description: n.title,
        icon: createElement(ReflexMark, { variant: 'tile', className: 'h-6 w-6' }),
        action: {
          label: 'View',
          onClick: () => navigate(withOrgSlug(slug, `/notifications/${n.id}`)),
        },
      });
    }
  }, [query.data, navigate, slug]);

  useEffect(
    () =>
      socket.onMessage((msg) => {
        if (msg.type === 'notifications:created') {
          void queryClient.invalidateQueries({ queryKey: notificationsKey(orgKey) });
        }
        if (msg.type === 'notifications:retracted') {
          const retractedId = msg.notificationId;
          // Optimistically strip from the list cache so it vanishes instantly.
          queryClient.setQueryData<ListNotificationsResponse>(notificationsKey(orgKey), (old) => {
            if (!old) return old;
            return { notifications: old.notifications.filter((n) => n.id !== retractedId) };
          });
          // Also evict any single-notification cache entry.
          queryClient.removeQueries({ queryKey: notificationKey(orgKey, retractedId) });
          seenIdsRef.current?.delete(retractedId);
        }
      }),
    [orgKey, queryClient, socket],
  );
}

export function useNotification(id: string | undefined) {
  const orgKey = useOrgKey();
  return useQuery({
    queryKey: id ? notificationKey(orgKey, id) : (['notifications', orgKey, 'none'] as const),
    queryFn: () => notificationsApi.get(id as string),
    enabled: !!id,
  });
}

export function useDismissNotification() {
  const orgKey = useOrgKey();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.dismiss(id),
    onSuccess: (_data, id) => {
      queryClient.setQueryData<ListNotificationsResponse>(notificationsKey(orgKey), (old) => {
        if (!old) return old;
        return {
          notifications: old.notifications.map((n) =>
            n.id === id ? { ...n, dismissed: true } : n,
          ),
        };
      });
      queryClient.setQueryData<Notification>(notificationKey(orgKey, id), (old) => {
        if (!old) return old;
        return { ...old, dismissed: true };
      });
    },
  });
}

/**
 * Dismiss several notifications as one user action. Fans out to per-id
 * `POST /notifications/:id/dismiss` calls in parallel (the server endpoint
 * is idempotent) and patches the list/detail caches in one pass so every
 * row in the group vanishes together instead of one-by-one. Used by the
 * inbox's collapsed release group so "dismiss this row" really means
 * "dismiss every release queued inside it".
 */
export function useDismissNotifications() {
  const orgKey = useOrgKey();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => Promise.all(ids.map((id) => notificationsApi.dismiss(id))),
    onSuccess: (_data, ids) => {
      const idSet = new Set(ids);
      queryClient.setQueryData<ListNotificationsResponse>(notificationsKey(orgKey), (old) => {
        if (!old) return old;
        return {
          notifications: old.notifications.map((n) =>
            idSet.has(n.id) ? { ...n, dismissed: true } : n,
          ),
        };
      });
      for (const id of ids) {
        queryClient.setQueryData<Notification>(notificationKey(orgKey, id), (old) =>
          old ? { ...old, dismissed: true } : old,
        );
      }
    },
  });
}
